import * as React from 'react'

export interface SummaryLeaderboardEntry {
  rank: number
  name: string
  visibility: number
  isYou: boolean
}

interface IntelligenceSummaryBarProps {
  visibility: number
  avgPosition: number | null
  leaderboardRank: number | null
  totalCompetitors: number
  leaderboard: SummaryLeaderboardEntry[]
  isLoading?: boolean
}

// ── Shared card shell ─────────────────────────────────────────────────────────

function SummaryCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[176px] flex-col gap-5 rounded-lg border border-border bg-surface p-6">
      {children}
    </div>
  )
}

function CardLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{children}</span>
}

// ── Card 1: Your Visibility ────────────────────────────────────────────────────

function VisibilityCard({ visibility }: { visibility: number }) {
  const pct = Math.min(Math.max(visibility, 0), 100)
  return (
    <SummaryCard>
      <div className="flex flex-col gap-1.5">
        <CardLabel>Your Visibility</CardLabel>
        <span className="text-[34px] font-semibold leading-none tabular-nums text-brand-accent">{Math.round(pct)}%</span>
        <span className="text-xs text-text-muted">of AI responses</span>
      </div>
      <div>
        <div className="relative h-1.5 rounded-full bg-surface-overlay">
          <div className="h-1.5 rounded-full bg-brand-accent transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] text-text-muted">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>
    </SummaryCard>
  )
}

// ── Card 2: Avg Position ───────────────────────────────────────────────────────

function AvgPositionCard({ avgPosition }: { avgPosition: number | null }) {
  const needlePct = avgPosition !== null ? Math.min(Math.max(((10 - avgPosition) / 9) * 100, 0), 100) : 0
  return (
    <SummaryCard>
      <div className="flex flex-col gap-1.5">
        <CardLabel>Avg Position</CardLabel>
        <span className="text-[34px] font-semibold leading-none tabular-nums text-[#8b5cf6]">
          {avgPosition !== null ? `#${avgPosition.toFixed(1)}` : '—'}
        </span>
        <span className="text-xs text-text-muted">when mentioned</span>
      </div>
      <div>
        <div className="relative h-1.5 rounded-full bg-surface-overlay">
          <div className="h-1.5 rounded-full bg-[#8b5cf6] transition-all duration-700" style={{ width: `${needlePct}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] text-text-muted">
          <span>#10</span>
          <span>#1 best</span>
        </div>
      </div>
    </SummaryCard>
  )
}

// ── Card 3: Leaderboard Rank ───────────────────────────────────────────────────

const BAR_HEIGHTS = [100, 56, 42, 30, 22, 18, 14, 11, 9, 7]

function LeaderboardRankCard({ leaderboardRank, totalCompetitors }: { leaderboardRank: number | null; totalCompetitors: number }) {
  return (
    <SummaryCard>
      <div className="flex flex-col gap-1.5">
        <CardLabel>Leaderboard Rank</CardLabel>
        <span className="text-[34px] font-semibold leading-none tabular-nums text-feedback-success">
          {leaderboardRank !== null ? `#${leaderboardRank}` : '—'}
        </span>
        <span className="text-xs text-text-muted">of {totalCompetitors} tracked brands</span>
      </div>
      <div className="flex h-9 items-end gap-1">
        {BAR_HEIGHTS.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm"
            style={{
              height: `${h}%`,
              backgroundColor: i === 0 ? 'var(--feedback-success)' : `rgba(22, 163, 74, ${Math.max(0.06, 0.2 - i * 0.015)})`,
            }}
          />
        ))}
      </div>
    </SummaryCard>
  )
}

// ── Card 4: Your Position (mini leaderboard) ───────────────────────────────────

type RowItem =
  | { type: 'entry'; entry: SummaryLeaderboardEntry }
  | { type: 'top' }
  | { type: 'bottom' }
  | { type: 'empty' }

function buildRows(leaderboard: SummaryLeaderboardEntry[], yourRank: number | null): RowItem[] {
  const EMPTY: RowItem = { type: 'empty' }
  if (yourRank === null || leaderboard.length === 0) return [EMPTY, EMPTY, EMPTY, EMPTY]

  const byRank = (rank: number): RowItem => {
    const entry = leaderboard.find((e) => e.rank === rank)
    return entry ? { type: 'entry', entry } : EMPTY
  }

  const youEntry = leaderboard.find((e) => e.isYou)
  if (!youEntry) return [EMPTY, EMPTY, EMPTY, EMPTY]

  const maxRank = Math.max(...leaderboard.map((e) => e.rank))
  if (yourRank === 1) return [{ type: 'top' }, { type: 'entry', entry: youEntry }, byRank(2), byRank(3)]
  if (yourRank === maxRank) {
    return [yourRank - 2 >= 1 ? byRank(yourRank - 2) : { type: 'top' }, byRank(yourRank - 1), { type: 'entry', entry: youEntry }, { type: 'bottom' }]
  }
  if (yourRank + 2 > maxRank) {
    return [yourRank - 2 >= 1 ? byRank(yourRank - 2) : { type: 'top' }, byRank(yourRank - 1), { type: 'entry', entry: youEntry }, byRank(yourRank + 1)]
  }
  return [byRank(yourRank - 1), { type: 'entry', entry: youEntry }, byRank(yourRank + 1), byRank(yourRank + 2)]
}

function YourPositionCard({ leaderboard, leaderboardRank }: { leaderboard: SummaryLeaderboardEntry[]; leaderboardRank: number | null }) {
  const rows = buildRows(leaderboard, leaderboardRank)
  const maxVis = Math.max(...leaderboard.map((e) => e.visibility), 1)

  return (
    <SummaryCard>
      <CardLabel>Your Position</CardLabel>
      <div className="flex flex-col gap-1">
        {rows.map((row, i) => {
          if (row.type === 'top' || row.type === 'bottom') {
            return (
              <div key={row.type} className="flex items-center gap-2 px-1 py-1">
                <span className="w-4 shrink-0 text-right text-[11px] text-text-muted">—</span>
                <span className="text-[11px] italic text-text-muted">{row.type === 'top' ? 'top of board' : 'bottom of board'}</span>
              </div>
            )
          }
          if (row.type === 'empty') {
            return (
              <div key={`empty-${i}`} className="flex h-[26px] items-center gap-2 px-1 py-1">
                <span className="w-4 shrink-0 text-right text-[11px] text-text-muted">—</span>
                <div className="h-2 flex-1 rounded bg-surface-overlay" />
              </div>
            )
          }
          const { entry } = row
          return (
            <div
              key={entry.rank}
              className={`flex items-center gap-2 rounded-md px-1 py-1 ${entry.isYou ? 'border border-brand-accent/20 bg-brand-accent/10' : ''}`}
            >
              <span className={`w-4 shrink-0 text-right text-[11px] font-medium ${entry.isYou ? 'text-brand-accent' : 'text-text-muted'}`}>
                #{entry.rank}
              </span>
              <span className={`flex-1 truncate text-xs ${entry.isYou ? 'font-medium text-text' : 'text-text-muted'}`}>
                {entry.name}
              </span>
              <div className="w-9 shrink-0">
                <div className="h-[3px] w-full overflow-hidden rounded-full bg-surface-overlay">
                  <div
                    className={`h-[3px] rounded-full transition-all duration-700 ${entry.isYou ? 'bg-brand-accent' : 'bg-text-muted'}`}
                    style={{ width: `${(entry.visibility / maxVis) * 100}%` }}
                  />
                </div>
              </div>
              <span className={`w-7 shrink-0 text-right text-[11px] ${entry.isYou ? 'text-brand-accent' : 'text-text-muted'}`}>
                {entry.visibility}%
              </span>
            </div>
          )
        })}
      </div>
    </SummaryCard>
  )
}

// ── Skeleton ────────────────────────────────────────────────────────────────────

function SkeletonBar() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="min-h-[176px] animate-pulse rounded-lg border border-border bg-surface p-6">
          <div className="mb-2 h-2.5 w-20 rounded bg-surface-overlay" />
          <div className="mb-2 h-7 w-14 rounded bg-surface-overlay" />
          <div className="h-2.5 w-16 rounded bg-surface-overlay" />
        </div>
      ))}
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────────────────────────

export function IntelligenceSummaryBar({
  visibility,
  avgPosition,
  leaderboardRank,
  totalCompetitors,
  leaderboard,
  isLoading = false,
}: IntelligenceSummaryBarProps) {
  if (isLoading) return <SkeletonBar />

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      <VisibilityCard visibility={visibility} />
      <AvgPositionCard avgPosition={avgPosition} />
      <LeaderboardRankCard leaderboardRank={leaderboardRank} totalCompetitors={totalCompetitors} />
      <YourPositionCard leaderboard={leaderboard} leaderboardRank={leaderboardRank} />
    </div>
  )
}
